"use client";

import { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import PizZip from "pizzip";
import { PDFDocument } from "pdf-lib";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import axios from "axios";
import { saveAs } from "file-saver";
import { NumericFormat } from "react-number-format";
import AlertDialog from "../components/AlertDialog";

export default function SOVTab({ project, setProject }) {
  const getAddressString = (address = {}) =>
    `${address.street || ""}, ${address.city || ""}, ${address.state || ""}, ${
      address.country || ""
    } ${address.postalCode || ""}`;

  const [sovData, setSovData] = useState(() => {
    const account = project.accountId || {};
    return {
      billTo: account.companyName || "",
      invoiceDate: "",
      totalInvoiced: "0.00",
      amountPaid: "0.00",

      dueDate: "",
      projectName: project.projectName || "",
      job: getAddressString(project.address),
      accountName: account.companyName || "",
      accountAddress: getAddressString(account.address),
      items: [],
      withHoldback: false,
      status: "new",
      generated: false,
      qbInvoiceNumber: null,
      qbInvoiceId: null,
      sentAt: null,
      sendToEmail: project.billingContact?.email || "",
      specialInstructions: "",
    };
  });
  const [sovs, setSovs] = useState([]);
  const [approvedChangeOrders, setApprovedChangeOrders] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [quickBooksItems, setQuickBooksItems] = useState([]);
  const [quickBooksTaxRates, setQuickBooksTaxRates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSovId, setEditingSovId] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertType, setAlertType] = useState("error");
  const [alertTitle, setAlertTitle] = useState("Error");
  const [alertMessage, setAlertMessage] = useState("");
  const [isCustomBreakdown, setIsCustomBreakdown] = useState(false);
  const [taxRateData, setTaxRateData] = useState({
    taxRate: "0",
    taxCodeId: "",
  });
  const [customBreakdownItems, setCustomBreakdownItems] = useState([
    { description: "", amount: "0.00" },
  ]);
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: `Dear Client,\n\nPlease find attached the Schedule of Values for your review.\n\nBest regards,`,
    signature: "",
  });

  const API = "https://erp-xe9u.onrender.com/api";

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No authentication token found");

        const decoded = jwtDecode(token);
        setUserEmail(decoded.email || "");

        const contactEmail =
          sovData.sendToEmail || project.billingContact?.email || "";
        const clientFirstName = project.billingContact?.firstName || "Client";
        const response = await axios.get(`${API}/users/me/${decoded.email}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const displayName = response.data.success
          ? response.data.user.displayName || "User"
          : "User";

        setEmailForm((prev) => ({
          ...prev,
          to: contactEmail,
          subject: `${project.projectName} - SOV Invoice#${
            sovData.qbInvoiceNumber || "Pending"
          }`,
          body: `Dear ${clientFirstName},\n\nPlease find attached the Schedule of Values for your review.\n\nBest regards,`,
          signature: displayName,
        }));
      } catch (err) {
        setAlertType("error");
        setAlertTitle("User Data Error");
        setAlertMessage("Failed to load user information.");
        setAlertOpen(true);
      }
    };
    fetchUserData();
  }, [
    project.projectName,
    sovData.sendToEmail,
    sovData.qbInvoiceNumber,
    project.billingContact?.firstName,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }
        // Fetch the latest project data
        const projectResponse = await axios.get(
          `${API}/projects/${project._id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const updatedProject = projectResponse.data;
        setProject(updatedProject);
        // Fetch SOVs, company profile, and tax rate
        const [sovResponse, companyProfileResponse, taxRateResponse] =
          await Promise.all([
            fetch(`${API}/sovs/project/${project._id}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/company-profile/`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API}/company-profile/tax-rate`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
        if (!sovResponse.ok) {
          const errorData = await sovResponse.json();
          throw new Error(
            errorData.message || `SOV fetch error: ${sovResponse.status}`,
          );
        }
        const sovData = await sovResponse.json();
        const isQuickBooksConnected =
          taxRateResponse.data.isQuickBooksConnected || false;
        const [quickBooksItemsResponse, quickBooksTaxRatesResponse] =
          await Promise.all([
            isQuickBooksConnected
              ? axios.get(`${API}/api/quickbooks/items`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
              : Promise.resolve({ data: [] }),
            isQuickBooksConnected
              ? axios.get(`${API}/api/quickbooks/tax-codes`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
              : Promise.resolve({ data: [] }),
          ]);
        setSovs(sovData.scheduleOfValues);
        setCompanyProfile({
          ...companyProfileResponse.data,
          defaultTax: {
            taxRate: taxRateResponse.data.taxRate || "0",
            taxCodeId: taxRateResponse.data.taxCodeId || "",
            defaultItemId: taxRateResponse.data.defaultItemId || "",
          },
          plugins: {
            quickbooks: { connected: isQuickBooksConnected },
          },
        });
        setQuickBooksItems(quickBooksItemsResponse.data);
        setQuickBooksTaxRates(quickBooksTaxRatesResponse.data);
        setTaxRateData({
          taxRate: taxRateResponse.data.taxRate || "0",
          taxCodeId: taxRateResponse.data.taxCodeId || "",
        });
        // Include only billable change orders with "Approved" or "Confirmed" status
        const approvedCOs =
          updatedProject.changeOrders?.filter(
            (co) =>
              co.summary?.billable === true &&
              (co.approvalStatus === "Approved" ||
                co.approvalStatus === "Confirmed"),
          ) || [];
        setApprovedChangeOrders(approvedCOs);
        console.log("Billable Approved/Confirmed Change Orders:", approvedCOs); // Debug
      } catch (err) {
        setAlertType("error");
        setAlertTitle("Data Fetch Error");
        setAlertMessage(`Failed to load data: ${err.message}`);
        setAlertOpen(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [project._id, setProject]);

  const handleSOVChange = (e) => {
    const { name, value } = e.target;
    setSovData((prev) => {
      const updatedSov = { ...prev, [name]: value };
      if (name === "status" && value === "paid" && prev.amountPaid === "0.00") {
        updatedSov.amountPaid = calculateTotalInvoiced();
      }
      return updatedSov;
    });
  };

  // Re-assigns itemNumber = 1, 2, 3… to every line in the array
  const renumberItems = (items) => {
    return items.map((it, idx) => ({
      ...it,
      itemNumber: idx + 1,
    }));
  };

  const handleSendEmail = () => {
    if (!userEmail) {
      setAlertType("error");
      setAlertTitle("Error");
      setAlertMessage("User email not available. Please log in again.");
      setAlertOpen(true);
      return;
    }
    generateSOVPDF(false, true);
  };

  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject || !emailForm.body) {
        setAlertType("error");
        setAlertTitle("Invalid Input");
        setAlertMessage("Please fill in all required email fields");
        setAlertOpen(true);
        return;
      }

      const validateEmails = (emails) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails
          .split(",")
          .map((email) => email.trim())
          .every((email) => emailRegex.test(email));
      };

      if (!validateEmails(emailForm.to)) {
        setAlertType("error");
        setAlertTitle("Invalid Email");
        setAlertMessage("Please enter valid email addresses");
        setAlertOpen(true);
        return;
      }

      if (
        !(pdfBuffer instanceof Blob) ||
        pdfBuffer.type !== "application/pdf"
      ) {
        setAlertType("error");
        setAlertTitle("Missing or Invalid Attachment");
        setAlertMessage("A valid PDF file is required");
        setAlertOpen(true);
        return;
      }

      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("to", emailForm.to);
      formData.append("subject", emailForm.subject);
      formData.append("body", emailForm.body);
      formData.append("signature", emailForm.signature || "");
      formData.append("from", userEmail);

      formData.append(
        "files",
        pdfBuffer,
        `SOV_${project.projectNumber || "Project"}_${
          sovData.qbInvoiceNumber || new Date().toISOString().split("T")[0]
        }.pdf`,
      );

      const response = await axios.post(
        `${API}/api/sovs/${sovData._id}/send-email`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setShowEmailForm(false);
      setPdfBuffer(null);

      if (response.status === 200) {
        setAlertType("success");
        setAlertTitle("Success");
        setAlertMessage("Email sent successfully!");
        setAlertOpen(true);
      }
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Error");
      setAlertMessage(
        `Failed to send email: ${err.response?.data?.message || err.message}`,
      );
      setAlertOpen(true);
    }
  };

  const handleItemChange = (index, field, value) => {
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      let formattedValue = value;

      if (
        [
          "scheduledValue",
          "workCompletedPreviously",
          "workCompletedThisPeriod",

          "taxRate",
        ].includes(field)
      ) {
        const numValue = parseFloat(value) || 0;
        formattedValue = numValue.toFixed(2);

        if (field === "workCompletedThisPeriod") {
          const prevInvoiced = calculatePreviousInvoiced(
            updatedItems[index].description,
            editingSovId,
          );
          const scheduledValue =
            parseFloat(updatedItems[index].scheduledValue) || 0;
          const maxAllowed = scheduledValue - prevInvoiced;
          formattedValue = Math.min(numValue, maxAllowed).toFixed(2);
        } else if (field === "taxRate") {
          formattedValue = numValue.toFixed(2);
        }
      } else if (field === "taxCodeId") {
        formattedValue = value;
      } else {
        formattedValue = value;
      }

      updatedItems[index] = { ...updatedItems[index], [field]: formattedValue };

      const totalCompleted =
        (parseFloat(updatedItems[index].workCompletedPreviously) || 0) +
        (parseFloat(updatedItems[index].workCompletedThisPeriod) || 0);
      const scheduledValue =
        parseFloat(updatedItems[index].scheduledValue) || 0;
      updatedItems[index].totalCompletedToDate = totalCompleted.toFixed(2);
      updatedItems[index].percentage = scheduledValue
        ? ((totalCompleted / scheduledValue) * 100).toFixed(2)
        : "0.00";
      updatedItems[index].balanceToFinish = (
        scheduledValue - totalCompleted
      ).toFixed(2);

      if (field === "description" && isCustomBreakdown) {
        setCustomBreakdownItems((prevItems) =>
          prevItems.map((bdItem, bdIndex) =>
            bdIndex + 1 === updatedItems[index].itemNumber &&
            !approvedChangeOrders.some(
              (co) => `${co.changeOrderNumber} - ${co.title}` === value,
            )
              ? { ...bdItem, description: value }
              : bdItem,
          ),
        );
      }

      return { ...prev, items: updatedItems };
    });
  };

  const calculatePreviousInvoiced = (description, excludeSovId) => {
    const matchingItems = sovs
      .filter((sov) => sov._id !== excludeSovId)
      .flatMap((sov) => sov.items)
      .filter((item) => item.description === description);

    if (matchingItems.length === 0) {
      return 0; // Return 0 if no prior invoices match the description
    }

    return matchingItems.reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    );
  };

  const getAllContractItems = (
    previousItems = null,
    useCustomBreakdown = null,
    breakdownItems = null,
  ) => {
    const isBreakdown =
      useCustomBreakdown !== null ? useCustomBreakdown : isCustomBreakdown;
    const breakdown =
      breakdownItems !== null ? breakdownItems : customBreakdownItems;
    const originalContractValue =
      parseFloat(project.originalContractValue) || 0;
    const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxCodeId ||
        quickBooksTaxRates.find((tr) =>
          tr.taxRates.some((rate) => rate.rate === 0),
        )?.id ||
        quickBooksTaxRates[0]?.id ||
        ""
      : "";
    const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxRate || "0"
      : parseFloat(taxRateData.taxRate) || 0;
    const defaultItemId = companyProfile?.plugins?.quickbooks?.connected
      ? companyProfile.defaultTax.defaultItemId || quickBooksItems[0]?.id || ""
      : "";

    // Create a set of existing change order numbers from all SOVs to prevent duplicates
    const existingChangeOrderNumbers = new Set(
      sovs.flatMap(
        (sov) =>
          sov.items
            .filter((item) => item.changeOrderNumber)
            .map((item) => String(item.changeOrderNumber)), // Convert to string
      ),
    );

    console.log("existingChangeOrderNumbers:", [...existingChangeOrderNumbers]); // Debug

    let originalItems = [];
    if (isBreakdown && breakdown.length > 0) {
      // Include all custom breakdown items from breakdown
      const changeOrderDescriptions = approvedChangeOrders.map(
        (co) => `${co.changeOrderNumber} - ${co.title}`,
      );
      originalItems = breakdown.map((item, index) => {
        const description = item.description || `Breakdown Item ${index + 1}`;
        const scheduledValue = parseFloat(item.amount) || 0;
        const prevInvoiced = calculatePreviousInvoiced(
          description,
          editingSovId,
        );

        const prevItem = previousItems?.find(
          (pi) => pi.description === description,
        );
        const workCompletedThisPeriod =
          editingSovId && prevItem
            ? prevItem.workCompletedThisPeriod || "0.00"
            : "0.00";
        const totalCompleted = (
          prevInvoiced + parseFloat(workCompletedThisPeriod)
        ).toFixed(2);
        return {
          itemNumber: index + 1,
          description,
          taxCodeId: prevItem?.taxCodeId || defaultTaxCode,
          taxRate: prevItem?.taxRate || defaultTaxRate.toString(),
          scheduledValue: scheduledValue.toFixed(2),
          workCompletedPreviously: prevInvoiced.toFixed(2),
          workCompletedThisPeriod,
          totalCompletedToDate: totalCompleted,
          percentage: scheduledValue
            ? (
                (parseFloat(totalCompleted) / Math.abs(scheduledValue)) *
                100
              ).toFixed(2)
            : "0.00",
          balanceToFinish: (
            scheduledValue - parseFloat(totalCompleted)
          ).toFixed(2),

          qbItem: prevItem?.qbItem || defaultItemId,
          changeOrderNumber: null, // Non-change order item
        };
      });
    } else if (
      !previousItems ||
      !previousItems.some(
        (item) => item.description === "Original Contract Value",
      )
    ) {
      // Include Original Contract Value if not present
      const description = "Original Contract Value";
      const scheduledValue = originalContractValue.toFixed(2);
      const prevInvoiced = calculatePreviousInvoiced(description, editingSovId);

      const prevItem = previousItems?.find(
        (item) => item.description === description,
      );
      const workCompletedThisPeriod =
        editingSovId && prevItem
          ? prevItem.workCompletedThisPeriod || "0.00"
          : "0.00";
      const totalCompleted = (
        prevInvoiced + parseFloat(workCompletedThisPeriod)
      ).toFixed(2);
      originalItems = [
        {
          itemNumber: 1,
          description,
          taxCodeId: prevItem?.taxCodeId || defaultTaxCode,
          taxRate: prevItem?.taxRate || defaultTaxRate.toString(),
          scheduledValue,
          workCompletedPreviously: prevInvoiced.toFixed(2),
          workCompletedThisPeriod,
          totalCompletedToDate: totalCompleted,
          percentage: originalContractValue
            ? (
                (parseFloat(totalCompleted) / originalContractValue) *
                100
              ).toFixed(2)
            : "0.00",
          balanceToFinish: (
            originalContractValue - parseFloat(totalCompleted)
          ).toFixed(2),

          qbItem: prevItem?.qbItem || defaultItemId,
          changeOrderNumber: null, // Non-change order item
        },
      ];
    }

    // Include all change order items from previousItems
    const previousChangeOrderItems =
      previousItems
        ?.filter((item) => item.changeOrderNumber)
        .map((item, index) => {
          const prevInvoiced = calculatePreviousInvoiced(
            item.description,
            editingSovId,
          );

          const workCompletedThisPeriod = editingSovId
            ? item.workCompletedThisPeriod || "0.00"
            : "0.00";
          const totalCompleted = (
            prevInvoiced + parseFloat(workCompletedThisPeriod)
          ).toFixed(2);
          const scheduledValue = parseFloat(item.scheduledValue) || 0;
          return {
            itemNumber: originalItems.length + index + 1,
            description: item.description,
            changeOrderNumber: String(item.changeOrderNumber), // Ensure string
            changeOrderId: item.changeOrderId,
            taxCodeId: item.taxCodeId || defaultTaxCode,
            taxRate: item.taxRate || defaultTaxRate.toString(),
            scheduledValue: scheduledValue.toFixed(2),
            workCompletedPreviously: prevInvoiced.toFixed(2),
            workCompletedThisPeriod,
            totalCompletedToDate: totalCompleted,
            percentage: scheduledValue
              ? (
                  (parseFloat(totalCompleted) / Math.abs(scheduledValue)) *
                  100
                ).toFixed(2)
              : "0.00",
            balanceToFinish: (
              scheduledValue - parseFloat(totalCompleted)
            ).toFixed(2),

            qbItem: item.qbItem || defaultItemId,
          };
        }) || [];

    // Add new change orders from approvedChangeOrders, excluding duplicates by changeOrderNumber
    const newChangeOrderItems = approvedChangeOrders
      .filter(
        (co) => !existingChangeOrderNumbers.has(String(co.changeOrderNumber)),
      )
      .map((co, index) => {
        const description = `${co.changeOrderNumber} - ${co.title}`;
        const scheduledValue = parseFloat(co.totalPrice) || 0;
        const prevInvoiced = calculatePreviousInvoiced(
          description,
          editingSovId,
        );

        const workCompletedThisPeriod = editingSovId ? "0.00" : "0.00";
        const totalCompleted = (
          prevInvoiced + parseFloat(workCompletedThisPeriod)
        ).toFixed(2);
        return {
          itemNumber:
            originalItems.length + previousChangeOrderItems.length + index + 1,
          description,
          changeOrderNumber: String(co.changeOrderNumber), // Ensure string
          changeOrderId: co._id,
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: scheduledValue.toFixed(2),
          workCompletedPreviously: prevInvoiced.toFixed(2),
          workCompletedThisPeriod,
          totalCompletedToDate: totalCompleted,
          percentage: scheduledValue
            ? (
                (parseFloat(totalCompleted) / Math.abs(scheduledValue)) *
                100
              ).toFixed(2)
            : "0.00",
          balanceToFinish: (
            scheduledValue - parseFloat(totalCompleted)
          ).toFixed(2),

          qbItem: defaultItemId,
        };
      });

    const finalItems = [
      ...originalItems,
      ...previousChangeOrderItems,
      ...newChangeOrderItems,
    ];
    console.log("originalItems:", originalItems); // Debug: Should show 7 items
    console.log("previousChangeOrderItems:", previousChangeOrderItems); // Debug: Should show 9 items
    console.log("newChangeOrderItems:", newChangeOrderItems); // Debug: Should show new change orders
    console.log("finalItems:", finalItems); // Debug: Should show 16+ items
    return finalItems;
  };

  const addLineItem = () => {
    setSovData((prev) => {
      if (prev.status === "voided") return prev;
      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? companyProfile.defaultTaxRate ||
          quickBooksTaxRates.find((tr) =>
            tr.taxRates.some((rate) => rate.rate === 0),
          )?.id ||
          quickBooksTaxRates[0]?.id ||
          ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? ""
        : parseFloat(companyProfile?.defaultTaxRate) || 0;
      const updatedItems = [
        ...prev.items,
        {
          itemNumber: prev.items.length + 1,
          description: "",
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: "0.00",
          workCompletedPreviously: "0.00",
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: "0.00",
          percentage: "0.00",
          balanceToFinish: "0.00",

          qbItem: "",
        },
      ];
      return { ...prev, items: updatedItems };
    });
  };

  const removeLineItem = (index) => {
    setSovData((prev) => {
      if (prev.status === "voided") return prev;
      const updatedItems = prev.items
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, itemNumber: i + 1 }));
      return { ...prev, items: updatedItems };
    });
  };

  const moveItemUp = (index) => {
    if (index === 0 || sovData.status === "voided") return;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      [updatedItems[index - 1], updatedItems[index]] = [
        updatedItems[index],
        updatedItems[index - 1],
      ];
      updatedItems.forEach((item, i) => (item.itemNumber = i + 1));
      return { ...prev, items: updatedItems };
    });
  };

  const moveItemDown = (index) => {
    if (index === sovData.items.length - 1 || sovData.status === "voided")
      return;
    setSovData((prev) => {
      const updatedItems = [...prev.items];
      [updatedItems[index], updatedItems[index + 1]] = [
        updatedItems[index + 1],
        updatedItems[index],
      ];
      updatedItems.forEach((item, i) => (item.itemNumber = i + 1));
      return { ...prev, items: updatedItems };
    });
  };

  const handleBreakdownItemChange = (index, field, value) => {
    // 1. Update breakdown list
    setCustomBreakdownItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    // 2. Sync with SOV items (non-CO rows only)
    setSovData((prev) => {
      const updatedItems = prev.items.map((item, i) => {
        if (
          i === index &&
          !approvedChangeOrders.some(
            (co) =>
              `${co.changeOrderNumber} - ${co.title}` === item.description,
          )
        ) {
          if (field === "description") {
            return {
              ...item,
              description: value || `Breakdown Item ${index + 1}`,
            };
          }
          if (field === "amount") {
            const scheduledValue = (parseFloat(value) || 0).toFixed(2);
            const totalCompleted =
              (parseFloat(item.workCompletedPreviously) || 0) +
              (parseFloat(item.workCompletedThisPeriod) || 0);
            return {
              ...item,
              scheduledValue,
              totalCompletedToDate: totalCompleted.toFixed(2),
              percentage: parseFloat(scheduledValue)
                ? ((totalCompleted / parseFloat(scheduledValue)) * 100).toFixed(
                    2,
                  )
                : "0.00",
              balanceToFinish: (
                parseFloat(scheduledValue) - totalCompleted
              ).toFixed(2),
            };
          }
        }
        return item;
      });

      // Re-build full list to keep COs in sync
      return {
        ...prev,
        items: buildFinalItems(
          updatedItems
            .filter((_, i) => i < customBreakdownItems.length)
            .map((item, i) => ({
              description:
                i === index && field === "description"
                  ? value
                  : item.description,
              amount:
                i === index && field === "amount" ? value : item.scheduledValue,
            })),
          updatedItems,
        ),
      };
    });
  };

  /*** ADD THIS AT THE TOP OF THE COMPONENT (after imports) ***/
  const isIgnoredBreakdownItem = (description = "") => {
    // CN, CQ or T&M anywhere in the string – case-insensitive
    return /(CN|CQ|T&M)/i.test(description);
  };

  const calculateRemainingBreakdown = () => {
    const originalItemsTotal = customBreakdownItems
      .filter((item) => {
        const desc = item.description || "";

        // 1. CN / CQ / T&M → ignore completely
        if (isIgnoredBreakdownItem(desc)) return false;

        // 2. Regular change-order → exclude from *original* breakdown
        const isChangeOrder = approvedChangeOrders.some(
          (co) =>
            desc.includes(co.changeOrderNumber) || desc.includes(co.title),
        );

        return !isChangeOrder;
      })
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const originalContractValue =
      parseFloat(project.originalContractValue) || 0;
    return (originalContractValue - originalItemsTotal).toFixed(2);
  };

  /*** --------------------------------------------------------------
   *  Build final SOV items from custom breakdown + change orders
   *  -------------------------------------------------------------- */
  const buildFinalItems = (breakdownItems, previousItems = []) => {
    const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxCodeId ||
        quickBooksTaxRates.find((tr) =>
          tr.taxRates.some((rate) => rate.rate === 0),
        )?.id ||
        quickBooksTaxRates[0]?.id ||
        ""
      : "";
    const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
      ? taxRateData.taxRate || "0"
      : parseFloat(taxRateData.taxRate) || 0;
    const defaultItemId = companyProfile?.plugins?.quickbooks?.connected
      ? companyProfile.defaultTax?.defaultItemId || quickBooksItems[0]?.id || ""
      : "";

    // ---- 1. Keep EVERY item that already existed in the last SOV ----
    const baseItems = previousItems.map((it) => ({
      ...it,
      workCompletedPreviously: calculatePreviousInvoiced(
        it.description,
        null,
      ).toFixed(2),

      workCompletedThisPeriod: it.workCompletedThisPeriod || "0.00",
      totalCompletedToDate: (
        parseFloat(calculatePreviousInvoiced(it.description, null)) +
        parseFloat(it.workCompletedThisPeriod || "0.00")
      ).toFixed(2),
      percentage: parseFloat(it.scheduledValue)
        ? (
            (parseFloat(
              (
                parseFloat(calculatePreviousInvoiced(it.description, null)) +
                parseFloat(it.workCompletedThisPeriod || "0.00")
              ).toFixed(2),
            ) /
              parseFloat(it.scheduledValue)) *
            100
          ).toFixed(2)
        : "0.00",
      balanceToFinish: (
        parseFloat(it.scheduledValue) -
        parseFloat(
          (
            parseFloat(calculatePreviousInvoiced(it.description, null)) +
            parseFloat(it.workCompletedThisPeriod || "0.00")
          ).toFixed(2),
        )
      ).toFixed(2),
    }));

    // ---- 2. Add ONLY new change-orders (by changeOrderNumber) ----
    const existingCoNumbers = new Set(
      baseItems
        .filter((i) => i.changeOrderNumber)
        .map((i) => String(i.changeOrderNumber)),
    );

    const newCoItems = approvedChangeOrders
      .filter((co) => !existingCoNumbers.has(String(co.changeOrderNumber)))
      .map((co, idx) => {
        const description = `${co.changeOrderNumber} - ${co.title}`;
        const scheduledValue = parseFloat(co.totalPrice) || 0;
        const prevInvoiced = calculatePreviousInvoiced(description, null);

        const totalCompleted = prevInvoiced.toFixed(2);

        return {
          itemNumber: baseItems.length + idx + 1,
          description,
          changeOrderNumber: String(co.changeOrderNumber),
          changeOrderId: co._id,
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: scheduledValue.toFixed(2),
          workCompletedPreviously: prevInvoiced.toFixed(2),
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: totalCompleted,
          percentage: scheduledValue
            ? ((parseFloat(totalCompleted) / scheduledValue) * 100).toFixed(2)
            : "0.00",
          balanceToFinish: (
            scheduledValue - parseFloat(totalCompleted)
          ).toFixed(2),

          qbItem: defaultItemId,
        };
      });

    // Re-number everything correctly
    return renumberItems([...baseItems, ...newCoItems]);
  };

  const addBreakdownItem = () => {
    setCustomBreakdownItems((prev) => [
      ...prev,
      { description: "", amount: "0.00" },
    ]);
    setSovData((prev) => {
      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? companyProfile.defaultTaxRate ||
          quickBooksTaxRates.find((tr) =>
            tr.taxRates.some((rate) => rate.rate === 0),
          )?.id ||
          quickBooksTaxRates[0]?.id ||
          ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? ""
        : parseFloat(companyProfile?.defaultTaxRate) || 0;
      const newItem = {
        itemNumber:
          prev.items.filter(
            (item) =>
              !approvedChangeOrders.some(
                (co) =>
                  `${co.changeOrderNumber} - ${co.title}` === item.description,
              ),
          ).length + 1,
        description: `Breakdown Item ${prev.items.length + 1}`,
        taxCodeId: defaultTaxCode,
        taxRate: defaultTaxRate.toString(),
        scheduledValue: "0.00",
        workCompletedPreviously: "0.00",
        workCompletedThisPeriod: "0.00",
        totalCompletedToDate: "0.00",
        percentage: "0.00",
        balanceToFinish: "0.00",

        qbItem: "",
      };
      const updatedItems = prev.items.map((item, index) => ({
        ...item,
        itemNumber: index + 1,
      }));
      return {
        ...prev,
        items: [
          ...updatedItems.filter(
            (item) =>
              !approvedChangeOrders.some(
                (co) =>
                  `${co.changeOrderNumber} - ${co.title}` === item.description,
              ),
          ),
          newItem,
          ...updatedItems.filter((item) =>
            approvedChangeOrders.some(
              (co) =>
                `${co.changeOrderNumber} - ${co.title}` === item.description,
            ),
          ),
        ],
      };
    });
  };
  const removeBreakdownItem = (index) => {
    setCustomBreakdownItems((prev) => {
      const removedItem = prev[index];
      const description =
        removedItem.description || `Breakdown Item ${index + 1}`;
      const sovItem = sovData.items.find(
        (item) => item.description === description,
      );

      if (sovItem) {
        const hasProgress =
          (parseFloat(sovItem.workCompletedPreviously) || 0) > 0 ||
          (parseFloat(sovItem.workCompletedThisPeriod) || 0) > 0;
        if (hasProgress) {
          setAlertType("error");
          setAlertTitle("Cannot Remove Item");
          setAlertMessage(
            `Cannot remove "${description}" because it has recorded work completed or payments.`,
          );
          setAlertOpen(true);
          return prev;
        }
      }

      if (prev.length === 1) {
        setAlertType("error");
        setAlertTitle("Cannot Remove Item");
        setAlertMessage("At least one breakdown item is required.");
        setAlertOpen(true);
        return prev;
      }

      const updatedBreakdownItems = prev.filter((_, i) => i !== index);
      setSovData((prevSov) => {
        const updatedSovItems = prevSov.items
          .filter((item) => item.description !== description)
          .map((item, i) => ({ ...item, itemNumber: i + 1 }));
        return { ...prevSov, items: updatedSovItems };
      });
      return updatedBreakdownItems;
    });
  };

  const validateBreakdownTotal = () => {
    const filteredTotal = customBreakdownItems
      .filter((item) => {
        const desc = item.description || "";

        if (isIgnoredBreakdownItem(desc)) return false;

        const isChangeOrder = approvedChangeOrders.some(
          (co) =>
            desc.includes(co.changeOrderNumber) || desc.includes(co.title),
        );

        return !isChangeOrder;
      })
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const originalContractValue =
      parseFloat(project.originalContractValue) || 0;

    if (filteredTotal.toFixed(2) !== originalContractValue.toFixed(2)) {
      return (
        `Breakdown total ($${filteredTotal.toFixed(
          2,
        )}) must equal Original Contract Value ` +
        `($${originalContractValue.toFixed(2)}). Remaining: $${(
          originalContractValue - filteredTotal
        ).toFixed(2)}.`
      );
    }
    return null;
  };
  const validateSOV = () => {
    for (const item of sovData.items) {
      const totalCompleted = parseFloat(item.totalCompletedToDate) || 0;

      // Validate QuickBooks item requirement
      if (
        companyProfile?.plugins?.quickbooks?.connected &&
        !item.qbItem &&
        parseFloat(item.workCompletedThisPeriod) > 0
      ) {
        return `QuickBooks Item is required for "${item.description}" when work completed this period is greater than 0.`;
      }
    }
    return null;
  };

  const generateSOVPDF = async (shouldDownload = true, sendEmail = false) => {
    if (!companyProfile) {
      setAlertType("error");
      setAlertTitle("PDF Generation Error");
      setAlertMessage("Cannot generate PDF: Company profile is not set.");
      setAlertOpen(true);
      return null;
    }

    console.log(companyProfile);

    try {
      const response = await fetch("/sov_template.docx");
      if (!response.ok) {
        throw new Error(`Failed to load SOV template: ${response.statusText}`);
      }
      const templateArrayBuffer = await response.arrayBuffer();

      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue)) {
          return null;
        }
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64)) {
          throw new Error("Error parsing base64 data, invalid characters");
        }
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      }

      let imageBase64 = null;
      if (companyProfile?.logo) {
        if (!companyProfile.logo.startsWith("data:image")) {
          const logoResponse = await fetch(companyProfile.logo);
          if (logoResponse.ok) {
            const logoBlob = await logoResponse.blob();
            imageBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(logoBlob);
            });
          }
        } else {
          imageBase64 = companyProfile.logo;
        }
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: () => [125, 90],
      };

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [new ImageModule(imageOptions)],
      });

      const companyName = companyProfile?.companyName || "Your Company Name";
      const companyAddress = companyProfile?.address
        ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
        : "N/A";

      const taxRate =
        sovData.items.length > 0
          ? companyProfile?.plugins?.quickbooks?.connected
            ? parseFloat(
                quickBooksTaxRates.find(
                  (tr) => tr.id === sovData.items[0].taxCodeId,
                )?.taxRates[0]?.rate || 0,
              )
            : parseFloat(sovData.items[0].taxRate) || 0
          : 0;

      const data = {
        companyName,
        companyAddress,
        AccountName:
          sovData.accountName || project.accountId?.companyName || "N/A",
        AccountAddress:
          sovData.accountAddress ||
          getAddressString(project.accountId?.address || {}),
        image: imageBase64,
        billTo: sovData.billTo,
        projectAddress: sovData.job,
        projectName: sovData.projectName || project.projectName || "N/A",
        originalContractValue: formatDollarValue(
          project.originalContractValue || 0,
        ),
        totalInvoiced: formatDollarValue(calculateTotalInvoiced()),
        qbInvoiceNumber: sovData.qbInvoiceNumber || "N/A",
        taxRate: taxRate + "%",
        items: sovData.items.map((item) => ({
          itemNumber: item.itemNumber,
          description: item.description,
          scheduledValue: formatDollarValue(item.scheduledValue),
          workCompletedPreviously: formatDollarValue(
            item.workCompletedPreviously,
          ),
          workCompletedThisPeriod: formatDollarValue(
            item.workCompletedThisPeriod,
          ),
          totalCompletedToDate: formatDollarValue(item.totalCompletedToDate),

          percentage: item.percentage,
          balanceToFinish: formatDollarValue(item.balanceToFinish),
        })),
        tsv: formatDollarValue(totalScheduledValue),
        twcp: formatDollarValue(totalWorkCompletedPreviously),
        twctp: formatDollarValue(thisPeriodSubtotal),
        tctd: formatDollarValue(totalCompletedToDate),

        tp: totalPercentage,
        tbf: formatDollarValue(totalBalanceToFinish),
        thisPeriodSubtotal: formatDollarValue(thisPeriodSubtotal),
        hst: formatDollarValue(hst),
        subTotal: formatDollarValue(subTotalWithTax),
        currentHoldback: formatDollarValue(currentHoldback),
        totalProjectHoldback: formatDollarValue(totalProjectHoldback),
        remainingBalance: formatDollarValue(remainingBalance),
        invoiceDate: new Date(sovData.invoiceDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        dueDate: new Date(sovData.dueDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        specialInstructions: sovData.specialInstructions || "",
      };

      doc.setData(data);
      doc.render();

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append(
        "docx",
        docxBlob,
        `SOV_${project.projectName}_${sovData.qbInvoiceNumber || "100"}.docx`,
      );
      formData.append("documentType", "SOV");
      formData.append("identifier", project._id || "default");

      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        `${API}/api/upload/convert-to-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });

        if (sendEmail) {
          setPdfBuffer(pdfBlob);
          setShowEmailForm(true);
        } else if (shouldDownload) {
          saveAs(
            pdfBlob,
            `SOV_${project.projectNumber || "Project"}_${
              sovData.qbInvoiceNumber || new Date().toISOString().split("T")[0]
            }.pdf`,
          );
        }

        return pdfBlob;
      } else {
        const textResponse = await pdfResponse.data.text();
        throw new Error(
          JSON.parse(textResponse)?.message || "Unknown error from server",
        );
      }
    } catch (err) {
      setAlertType("error");
      setAlertTitle("PDF Generation Error");
      setAlertMessage("Failed to generate SOV PDF: " + err.message);
      setAlertOpen(true);
      return null;
    }
  };

  const generateQBInvoice = async () => {
    if (isGeneratingInvoice) return;

    setIsGeneratingInvoice(true);
    try {
      const token = localStorage.getItem("token");
      const billingContact = project?.billingContact || {};
      const billingAccount = project?.accountId || {};

      const customer = {
        email: billingContact.email || "unknown@example.com",
        firstName: billingContact.firstName || "Unknown",
        lastName: billingContact.lastName || "Customer",
        companyName: billingAccount.companyName || "Unnamed Company",
        address: {
          street: billingAccount.address?.street || "",
          city: billingAccount.address?.city || "",
          state: billingAccount.address?.state || "",
          zip: billingAccount.address?.postalCode || "",
          country: billingAccount.address?.country || "",
        },
      };

      const thisPeriodSubtotal = sovData.items.reduce(
        (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
        0,
      );

      const lineItems = sovData.items
        .filter((item) => parseFloat(item.workCompletedThisPeriod) !== 0)
        .map((item) => ({
          qbItemId: item.qbItem,
          description: item.description,
          amount: parseFloat(item.workCompletedThisPeriod),
          taxCodeId: item.taxCodeId,
        }));

      const hst = sovData.items.reduce((sum, item) => {
        const taxRate = companyProfile?.plugins?.quickbooks?.connected
          ? quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)
              ?.taxRates[0]?.rate || 0
          : parseFloat(item.taxRate) || 0;
        return (
          sum +
          (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
        );
      }, 0);

      const preTaxHoldback = sovData.withHoldback
        ? thisPeriodSubtotal * 0.1
        : 0;

      if (sovData.withHoldback && preTaxHoldback > 0) {
        const holdbackItem = quickBooksItems.find((item) =>
          item.name.toLowerCase().includes("holdback"),
        ) || { id: quickBooksItems[0]?.id || "" };
        const taxCodeId =
          lineItems.length > 0
            ? lineItems[0].taxCodeId
            : companyProfile.defaultTaxRate ||
              quickBooksTaxRates.find((tr) =>
                tr.taxRates.some((rate) => rate.rate === 0),
              )?.id ||
              "";
        lineItems.push({
          qbItemId: holdbackItem.id,
          description: "Holdback (10%)",
          amount: -preTaxHoldback,
          taxCodeId,
        });
      }

      const totalInvoiced = calculateTotalInvoiced();

      const invoiceData = {
        customer,
        sovId: sovData._id,
        invoiceDate: sovData.invoiceDate,
        dueDate: sovData.dueDate,
        lineItems,
        totalInvoiced: parseFloat(totalInvoiced),
      };

      let response;
      if (sovData.qbInvoiceId) {
        // Update existing invoice
        response = await axios.put(
          `${API}/api/quickbooks/invoices`,
          {
            ...invoiceData,
            qbInvoiceNumber: sovData.qbInvoiceNumber,
            qbInvoiceId: sovData.qbInvoiceId, // ← add this
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      } else {
        // Create new invoice
        response = await axios.post(
          `${API}/api/quickbooks/invoices`,
          invoiceData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      const { qbInvoiceNumber, qbInvoiceId, pdf } = response.data;

      function base64ToBlob(base64, contentType = "application/pdf") {
        const byteCharacters = atob(base64);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);

          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType });
      }

      const invoiceBlob = base64ToBlob(pdf, "application/pdf");
      const sovBlob = await generateSOVPDF(false);
      if (!sovBlob) return;

      const invoiceBytes = await invoiceBlob.arrayBuffer();
      const sovBytes = await sovBlob.arrayBuffer();

      const mergedPdf = await PDFDocument.create();
      const invoiceDoc = await PDFDocument.load(invoiceBytes);
      const sovDoc = await PDFDocument.load(sovBytes);

      const invoicePages = await mergedPdf.copyPages(
        invoiceDoc,
        invoiceDoc.getPageIndices(),
      );
      invoicePages.forEach((page) => mergedPdf.addPage(page));

      const sovPages = await mergedPdf.copyPages(
        sovDoc,
        sovDoc.getPageIndices(),
      );
      sovPages.forEach((page) => mergedPdf.addPage(page));

      const mergedBytes = await mergedPdf.save();
      const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });

      saveAs(
        mergedBlob,
        `Invoice_SOV_${project.projectName || "Project"}_${
          qbInvoiceNumber || new Date().toISOString().split("T")[0]
        }.pdf`,
      );

      setSovData((prev) => ({
        ...prev,
        qbInvoiceNumber,
        qbInvoiceId,
        generated: true,
        status: "generated",
        sentAt: null,
      }));

      await saveSOV(
        {
          ...sovData,
          qbInvoiceNumber,
          qbInvoiceId,
          generated: true,
          status: "generated",
          totalInvoiced,
          sentAt: null,
        },
        { keepFormOpen: true },
      );

      setAlertType("success");
      setAlertTitle("Success");
      setAlertMessage(
        sovData.qbInvoiceId
          ? "QuickBooks invoice updated successfully!"
          : "QuickBooks invoice generated successfully!",
      );
      setAlertOpen(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "An unexpected error occurred.";

      setAlertType("error");
      setAlertTitle("QuickBooks Invoice Error");
      setAlertMessage(errorMessage);
      setAlertOpen(true);
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const voidQBInvoice = async () => {
    if (!sovData.qbInvoiceId) {
      setAlertType("error");
      setAlertTitle("Void Invoice Error");
      setAlertMessage("No QuickBooks invoice to void.");
      setAlertOpen(true);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/quickbooks/invoices/${sovData.qbInvoiceId}/void`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSovData((prev) => ({
        ...prev,
        status: "voided",
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        sentAt: null,
      }));
      await saveSOV({
        ...sovData,
        status: "voided",
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        totalInvoiced: "0.00",
        sentAt: null,
      });

      setAlertType("success");
      setAlertTitle("Success");
      setAlertMessage("QuickBooks invoice voided successfully!");
      setAlertOpen(true);
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Void Invoice Error");
      setAlertMessage("Failed to void QuickBooks invoice: " + err.message);
      setAlertOpen(true);
    }
  };

  const sendQBInvoice = async () => {
    if (!sovData.qbInvoiceId) {
      setAlertType("error");
      setAlertTitle("Send Invoice Error");
      setAlertMessage(
        "No QuickBooks invoice to send. Please generate one first.",
      );
      setAlertOpen(true);
      return;
    }

    if (sovData.sentAt) {
      setAlertType("error");
      setAlertTitle("Send Invoice Error");
      setAlertMessage("This invoice has already been sent.");
      setAlertOpen(true);
      return;
    }

    if (isSendingInvoice) return;

    setIsSendingInvoice(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        sovId: sovData._id,
      };
      if (sovData.sendToEmail) {
        payload.sendTo = sovData.sendToEmail;
      }

      const response = await axios.post(
        `${API}/api/quickbooks/invoices/${sovData.qbInvoiceId}/send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSovData((prev) => ({
        ...prev,
        status: "sent",
        sentAt: new Date(),
      }));

      await saveSOV({
        ...sovData,
        status: "sent",
        sentAt: new Date(),
      });

      setAlertType("success");
      setAlertTitle("Success");
      setAlertMessage("QuickBooks invoice sent successfully!");
      setAlertOpen(true);
    } catch (err) {
      let errorMessage =
        err.response?.data?.message || "Failed to send QuickBooks invoice";
      if (err.response?.data?.error === "Stale Object Error") {
        errorMessage = "The invoice was modified elsewhere. Please try again.";
      }
      setAlertType("error");
      setAlertTitle("Send Invoice Error");
      setAlertMessage(errorMessage);
      setAlertOpen(true);
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const handleViewSOV = (sov) => {
    const nonChangeOrderItems = sov.items.filter(
      (item) =>
        !approvedChangeOrders.some(
          (co) => `${co.changeOrderNumber} - ${co.title}` === item.description,
        ),
    );
    const isSovCustomBreakdown =
      nonChangeOrderItems.length > 1 || sov.isCustomBreakdown;
    setIsCustomBreakdown(isSovCustomBreakdown);
    setCustomBreakdownItems(
      isSovCustomBreakdown
        ? nonChangeOrderItems.map((item) => ({
            description: item.description,
            amount: item.scheduledValue,
          }))
        : [{ description: "", amount: "0.00" }],
    );
    setIsBreakdownExpanded(false);

    // Get current SOV's invoice date
    const currentSovDate = new Date(sov.invoiceDate);

    // Calculate previous invoiced based on SOVs with invoice date BEFORE current SOV
    const calculatePreviousInvoicedByDate = (description) => {
      return sovs
        .filter((s) => {
          const sovDate = new Date(s.invoiceDate);
          return s._id !== sov._id && sovDate < currentSovDate;
        })
        .flatMap((s) => s.items)
        .filter((item) => item.description === description)
        .reduce(
          (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
          0,
        );
    };

    // When viewing an existing SOV, calculate based on invoice date
    const updatedItems = sov.items.map((item, index) => {
      const workCompletedPreviously = calculatePreviousInvoicedByDate(
        item.description,
      );
      const workCompletedThisPeriod =
        parseFloat(item.workCompletedThisPeriod) || 0;
      const totalCompleted = workCompletedPreviously + workCompletedThisPeriod;
      const scheduledValue = parseFloat(item.scheduledValue) || 0;
      return {
        ...item,
        itemNumber: index + 1,
        workCompletedPreviously: workCompletedPreviously.toFixed(2),
        workCompletedThisPeriod: workCompletedThisPeriod.toFixed(2),
        totalCompletedToDate: totalCompleted.toFixed(2),
        percentage: scheduledValue
          ? ((totalCompleted / Math.abs(scheduledValue)) * 100).toFixed(2)
          : "0.00",
        balanceToFinish: (scheduledValue - totalCompleted).toFixed(2),
      };
    });
    setSovData({
      ...sov,
      items: updatedItems,
      amountPaid: sov.amountPaid || "0.00",

      accountName: sov.accountName || project.accountId?.companyName || "N/A",
      accountAddress:
        sov.accountAddress ||
        getAddressString(project.accountId?.address || {}),
      projectName: sov.projectName || project.projectName || "N/A",
      sendToEmail: sov.sendToEmail || project.billingContact?.email || "",
      specialInstructions: sov.specialInstructions || "",
    });
    setEditingSovId(sov._id);
    setShowCreateForm(true);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isCustomBreakdown) {
      const breakdownError = validateBreakdownTotal();
      if (breakdownError) {
        setAlertType("error");
        setAlertTitle("Breakdown Validation Error");
        setAlertMessage(breakdownError);
        setAlertOpen(true);
        return;
      }
    }

    const totalInvoiced = calculateTotalInvoiced();

    const dataToSubmit = {
      ...sovData,
      totalInvoiced,
      isCustomBreakdown,
    };

    await saveSOV(dataToSubmit);
  };

  const handleCreateSOV = async () => {
    try {
      const token = localStorage.getItem("token");
      const projectResponse = await axios.get(
        `${API}/api/projects/${project._id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedProject = projectResponse.data;
      setProject(updatedProject);

      // ---- All approved change-orders (including brand-new ones) ----
      const updatedApprovedCOs = (updatedProject.changeOrders || []).filter(
        (co) =>
          co.summary?.billable === true &&
          (co.approvalStatus === "Approved" ||
            co.approvalStatus === "Confirmed"),
      );
      setApprovedChangeOrders(updatedApprovedCOs);

      const latestSov = sovs.length > 0 ? sovs[sovs.length - 1] : null;

      /* --------------------------------------------------------------
       *  1. Decide if we are in custom-breakdown mode
       * -------------------------------------------------------------- */
      let useCustomBreakdown = false;
      let breakdownForNew = [{ description: "", amount: "0.00" }];

      if (latestSov) {
        const nonCo = latestSov.items
          .filter((i) => !i.changeOrderNumber)
          .map((i) => ({
            description: i.description,
            amount: i.scheduledValue,
          }));
        useCustomBreakdown = nonCo.length > 1 || !!latestSov.isCustomBreakdown;
        if (useCustomBreakdown) breakdownForNew = nonCo;
      }

      /* --------------------------------------------------------------
       *  2. Build the line-item array
       * -------------------------------------------------------------- */
      let allContractItems = [];

      // ---- Default tax / QB values (used for new items) ----
      const defaultTaxCode = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxCodeId ||
          quickBooksTaxRates.find((tr) =>
            tr.taxRates.some((rate) => rate.rate === 0),
          )?.id ||
          quickBooksTaxRates[0]?.id ||
          ""
        : "";
      const defaultTaxRate = companyProfile?.plugins?.quickbooks?.connected
        ? taxRateData.taxRate || "0"
        : parseFloat(taxRateData.taxRate) || 0;
      const defaultItemId = companyProfile?.plugins?.quickbooks?.connected
        ? companyProfile.defaultTax?.defaultItemId ||
          quickBooksItems[0]?.id ||
          ""
        : "";

      if (useCustomBreakdown) {
        // ---- Custom breakdown: use buildFinalItems (already renumbers) ----
        allContractItems = buildFinalItems(
          breakdownForNew,
          latestSov?.items || [],
        );
      } else {
        // ---- Original Contract + only NEW change-orders ----

        // 1. Original Contract line
        const originalContractValue =
          parseFloat(project.originalContractValue) || 0;
        const prevInvoiced = calculatePreviousInvoiced(
          "Original Contract Value",
          null,
        );

        const totalCompleted = prevInvoiced.toFixed(2);

        const originalItem = {
          description: "Original Contract Value",
          taxCodeId: defaultTaxCode,
          taxRate: defaultTaxRate.toString(),
          scheduledValue: originalContractValue.toFixed(2),
          workCompletedPreviously: prevInvoiced.toFixed(2),
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: totalCompleted,
          percentage: originalContractValue
            ? (
                (parseFloat(totalCompleted) / originalContractValue) *
                100
              ).toFixed(2)
            : "0.00",
          balanceToFinish: (
            originalContractValue - parseFloat(totalCompleted)
          ).toFixed(2),

          qbItem: defaultItemId,
          changeOrderNumber: null,
        };

        // 2. Only NEW change-orders (not in latestSov)
        const existingCoNumbers = new Set(
          (latestSov?.items || [])
            .filter((i) => i.changeOrderNumber)
            .map((i) => String(i.changeOrderNumber)),
        );

        const newChangeOrderItems = updatedApprovedCOs
          .filter((co) => !existingCoNumbers.has(String(co.changeOrderNumber)))
          .map((co, idx) => {
            const description = `${co.changeOrderNumber} - ${co.title}`;
            const scheduledValue = parseFloat(co.totalPrice) || 0;
            const prevInvoiced = calculatePreviousInvoiced(description, null);

            const totalCompleted = prevInvoiced.toFixed(2);

            return {
              description,
              changeOrderNumber: String(co.changeOrderNumber),
              changeOrderId: co._id,
              taxCodeId: defaultTaxCode,
              taxRate: defaultTaxRate.toString(),
              scheduledValue: scheduledValue.toFixed(2),
              workCompletedPreviously: prevInvoiced.toFixed(2),
              workCompletedThisPeriod: "0.00",
              totalCompletedToDate: totalCompleted,
              percentage: scheduledValue
                ? ((parseFloat(totalCompleted) / scheduledValue) * 100).toFixed(
                    2,
                  )
                : "0.00",
              balanceToFinish: (
                scheduledValue - parseFloat(totalCompleted)
              ).toFixed(2),

              qbItem: defaultItemId,
            };
          });

        // Combine and renumber
        allContractItems = renumberItems([
          originalItem,
          ...newChangeOrderItems,
        ]);
      }

      allContractItems = allContractItems.map((item) => {
        const workCompletedPreviously =
          parseFloat(item.workCompletedPreviously) || 0;
        const scheduledValue = parseFloat(item.scheduledValue) || 0;
        return {
          ...item,
          workCompletedThisPeriod: "0.00",
          totalCompletedToDate: workCompletedPreviously.toFixed(2),
          percentage: scheduledValue
            ? (
                (workCompletedPreviously / Math.abs(scheduledValue)) *
                100
              ).toFixed(2)
            : "0.00",
          balanceToFinish: (scheduledValue - workCompletedPreviously).toFixed(
            2,
          ),
        };
      });

      /* --------------------------------------------------------------
       *  3. Set UI state
       * -------------------------------------------------------------- */
      setIsCustomBreakdown(useCustomBreakdown);
      setCustomBreakdownItems(breakdownForNew);
      setSovData((prev) => ({
        ...prev,
        billTo: updatedProject.accountId?.companyName || "",
        projectName: updatedProject.projectName || "",
        job: getAddressString(updatedProject.address),
        accountName: updatedProject.accountId?.companyName || "",
        accountAddress: getAddressString(
          updatedProject.accountId?.address || {},
        ),
        sendToEmail: updatedProject.billingContact?.email || "",
        withHoldback: latestSov?.withHoldback ?? false,
        specialInstructions: latestSov?.specialInstructions || "",
        items: allContractItems,
        // Reset these fields for new SOV
        qbInvoiceNumber: null,
        qbInvoiceId: null,
        generated: false,
        status: "new",
        sentAt: null,
        invoiceDate: "",
        dueDate: "",
        totalInvoiced: "0.00",
      }));

      setIsBreakdownExpanded(false);
      setEditingSovId(null);
      setShowCreateForm(true);
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Create SOV Error");
      setAlertMessage(`Failed to create SOV: ${err.message}`);
      setAlertOpen(true);
    }
  };

  const saveSOV = async (dataToSubmit, { keepFormOpen = false } = {}) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setAlertType("error");
      setAlertTitle("Authentication Error");
      setAlertMessage("Please log in to submit SOV.");
      setAlertOpen(true);
      return;
    }

    try {
      const method = editingSovId ? "PUT" : "POST";
      const url = editingSovId
        ? `${API}/api/sovs/project/${project._id}/${editingSovId}`
        : `${API}/api/sovs/project/${project._id}`;

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...dataToSubmit,
          qbInvoiceNumber: dataToSubmit.qbInvoiceNumber,
          qbInvoiceId: dataToSubmit.qbInvoiceId,
          sentAt: dataToSubmit.sentAt,
          amountPaid: dataToSubmit.amountPaid,

          sendToEmail: dataToSubmit.sendToEmail,
          isCustomBreakdown: dataToSubmit.isCustomBreakdown,
          specialInstructions: dataToSubmit.specialInstructions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const data = await response.json();
      setSovs((prev) =>
        editingSovId
          ? prev.map((sov) => (sov._id === data._id ? data : sov))
          : [...prev, data],
      );
      setSovData(data);
      setEditingSovId(data._id);
      if (!keepFormOpen) {
        setShowCreateForm(false);
      }
    } catch (err) {
      setAlertType("error");
      setAlertTitle("Save SOV Error");
      setAlertMessage(`Failed to save SOV: ${err.message}`);
      setAlertOpen(true);
    }
  };

  const calculateTotalInvoiced = () => {
    const thisPeriodSubtotal = sovData.items.reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    );
    const hst = sovData.items.reduce((sum, item) => {
      console.log(companyProfile.plugins);
      const taxRate = companyProfile?.plugins?.quickbooks?.connected
        ? quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)?.taxRates[0]
            ?.rate || 0
        : parseFloat(item.taxRate) || 0;
      return (
        sum + (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
      );
    }, 0);
    const subTotalWithTax = thisPeriodSubtotal + hst;

    const totalHoldback = sovData.withHoldback ? subTotalWithTax * 0.1 : 0;
    return (subTotalWithTax - totalHoldback).toFixed(2);
  };

  const availableItems = [
    {
      description: isCustomBreakdown
        ? "Custom Breakdown"
        : "Original Contract Value",
      totalPrice: project.originalContractValue,
    },
    ...approvedChangeOrders.map((co) => ({
      description: `${co.changeOrderNumber} - ${co.title}`,
      totalPrice: co.totalPrice,
    })),
  ];

  const formatDollarValue = (num) =>
    Number.isFinite(parseFloat(num))
      ? `$${parseFloat(num).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "$0.00";

  if (isLoading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;

  const thisPeriodSubtotal = sovData.items
    .reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedThisPeriod) || 0),
      0,
    )
    .toFixed(2);
  const totalCompletedToDate = sovData.items
    .reduce(
      (sum, item) => sum + (parseFloat(item.totalCompletedToDate) || 0),
      0,
    )
    .toFixed(2);

  const hst = sovData.items
    .reduce((sum, item) => {
      const taxRate = companyProfile?.plugins?.quickbooks?.connected
        ? quickBooksTaxRates.find((tr) => tr.id === item.taxCodeId)?.taxRates[0]
            ?.rate || 0
        : parseFloat(item.taxRate) || 0;
      return (
        sum + (parseFloat(item.workCompletedThisPeriod) || 0) * (taxRate / 100)
      );
    }, 0)
    .toFixed(2);
  const subTotalWithTax = (
    parseFloat(thisPeriodSubtotal) + parseFloat(hst)
  ).toFixed(2);
  const currentHoldback = sovData.withHoldback
    ? (parseFloat(subTotalWithTax) * 0.1).toFixed(2)
    : "0.00";
  const totalProjectHoldback = sovData.withHoldback
    ? (
        parseFloat(totalCompletedToDate) *
        (1 +
          (companyProfile?.plugins?.quickbooks?.connected
            ? quickBooksTaxRates.find(
                (tr) => tr.id === companyProfile.defaultTaxRate,
              )?.taxRates[0]?.rate || 0
            : parseFloat(companyProfile?.defaultTaxRate) || 0) /
            100) *
        0.1
      ).toFixed(2)
    : "0.00";
  const remainingBalance = calculateTotalInvoiced();

  const totalScheduledValue = sovData.items
    .reduce((sum, item) => sum + (parseFloat(item.scheduledValue) || 0), 0)
    .toFixed(2);
  const totalWorkCompletedPreviously = sovData.items
    .reduce(
      (sum, item) => sum + (parseFloat(item.workCompletedPreviously) || 0),
      0,
    )
    .toFixed(2);
  const totalWorkCompletedThisPeriod = thisPeriodSubtotal;
  const totalBalanceToFinish = sovData.items
    .reduce((sum, item) => sum + (parseFloat(item.balanceToFinish) || 0), 0)
    .toFixed(2);
  const totalPercentage = totalScheduledValue
    ? (
        (parseFloat(totalCompletedToDate) / parseFloat(totalScheduledValue)) *
        100
      ).toFixed(2)
    : "0.00";

  return (
    <div className="space-y-6">
      {!showCreateForm && (
        <>
          <div className="flex space-x-4 mb-4"></div>
          <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
            <div className="flex justify-between mb-4 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-200">
                SOVs - {companyProfile?.companyName || "Company"}
              </h3>
              <button
                onClick={handleCreateSOV}
                className="bg-orange-400 text-white px-4 py-2 rounded-md hover:bg-orange-500 transition-colors duration-200"
              >
                Create Invoice
              </button>
            </div>
            {sovs.length === 0 ? (
              <p className="text-gray-400 px-6 py-4">No SOVs found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                        Job
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Invoice Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      {companyProfile?.plugins?.quickbooks?.connected && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          QB Invoice #
                        </th>
                      )}
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg actions-column">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {sovs.map((sov) => (
                      <tr
                        key={sov._id}
                        onClick={(e) => {
                          if (e.target.closest(".actions-column")) return;
                          handleViewSOV(sov);
                        }}
                        className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
                      >
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.job}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.invoiceDate}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.dueDate}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.billTo}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                          {sov.status}
                        </td>
                        {companyProfile?.plugins?.quickbooks?.connected && (
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                            {sov.qbInvoiceNumber || "N/A"}
                          </td>
                        )}
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right actions-column">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewSOV(sov);
                            }}
                            className="text-orange-400 hover:text-orange-300 transition-colors duration-200"
                          >
                            View/Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-lg shadow-xl bg-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 px-6 py-4">
              Available Contract Items
            </h3>
            <p className="text-sm text-gray-400 mb-4 px-6">
              All items are automatically included in new SOVs.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg">
                      Description
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tr-lg">
                      Total Price
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {availableItems.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-600 transition-colors duration-200"
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        {item.description}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-200">
                        {formatDollarValue(item.totalPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showEmailForm && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-600">
            <h2 className="text-xl font-semibold text-gray-200 mb-4">
              Send Email
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  From
                </label>
                <input
                  type="email"
                  value={userEmail}
                  disabled
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  To
                </label>
                <input
                  type="text"
                  value={emailForm.to}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, to: e.target.value })
                  }
                  placeholder="recipient@example.com, another@example.com"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, subject: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Body
                </label>
                <textarea
                  value={emailForm.body}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, body: e.target.value })
                  }
                  rows="5"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Signature
                </label>
                <textarea
                  value={emailForm.signature}
                  onChange={(e) =>
                    setEmailForm({ ...emailForm, signature: e.target.value })
                  }
                  rows="3"
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailForm(false);
                    setPdfBuffer(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEmailSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
                  disabled={!pdfBuffer}
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        type={alertType}
        title={alertTitle}
        message={alertMessage}
        confirmText="OK"
        cancelText="Close"
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => setAlertOpen(false)}
      />

      {showCreateForm && (
        <div className="bg-gray-700 p-6 rounded-lg shadow-xl border border-gray-600">
          <h3 className="text-lg font-semibold text-gray-200 mb-4">
            {editingSovId ? "Edit SOV" : "Create Schedule of Values"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Bill To
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  name="billTo"
                  value={sovData.billTo}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Special Instructions
                </label>
                <textarea
                  name="specialInstructions"
                  value={sovData.specialInstructions}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  rows="4"
                  placeholder="Enter any special instructions for the invoice"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Invoice Date
                </label>
                <input
                  autoComplete="off"
                  type="date"
                  name="invoiceDate"
                  value={sovData.invoiceDate}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Total Invoiced
                </label>
                <input
                  type="text"
                  value={formatDollarValue(calculateTotalInvoiced())}
                  readOnly
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Due Date
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={sovData.dueDate}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Job
                </label>
                <input
                  type="text"
                  name="job"
                  value={sovData.job}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Status
                </label>
                <select
                  name="status"
                  value={sovData.status}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                >
                  <option value="new">New</option>
                  <option value="invoiced">Invoiced</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="voided">Voided</option>
                </select>
              </div>
              {sovData.status === "paid" && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Amount Paid
                  </label>
                  <NumericFormat
                    value={sovData.amountPaid}
                    onValueChange={(values) =>
                      setSovData((prev) => ({
                        ...prev,
                        amountPaid: values.floatValue?.toFixed(2) || "0.00",
                      }))
                    }
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    disabled={sovData.status === "voided"}
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
                    placeholder="$0.00"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Include Holdback
                </label>
                <input
                  type="checkbox"
                  checked={sovData.withHoldback}
                  onChange={(e) =>
                    setSovData({ ...sovData, withHoldback: e.target.checked })
                  }
                  disabled={sovData.status === "voided"}
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
              {companyProfile?.plugins?.quickbooks?.connected && (
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    QB Invoice Number
                  </label>
                  <input
                    type="text"
                    value={sovData.qbInvoiceNumber || "Not Generated"}
                    readOnly
                    className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Send To Email
                </label>
                <input
                  type="email"
                  name="sendToEmail"
                  value={sovData.sendToEmail}
                  onChange={handleSOVChange}
                  disabled={sovData.status === "voided"}
                  className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Custom Breakdown for Original Contract
                </label>
                <input
                  type="checkbox"
                  checked={isCustomBreakdown}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsCustomBreakdown(checked);

                    if (!checked) {
                      // ---- CUSTOM BREAKDOWN OFF ----
                      // 1. Reset the breakdown list
                      setCustomBreakdownItems([
                        { description: "", amount: "0.00" },
                      ]);

                      // 2. Build ONLY the Original Contract line + change-orders
                      const originalContractValue =
                        parseFloat(project.originalContractValue) || 0;
                      const defaultTaxCode = companyProfile?.plugins?.quickbooks
                        ?.connected
                        ? taxRateData.taxCodeId ||
                          quickBooksTaxRates.find((tr) =>
                            tr.taxRates.some((rate) => rate.rate === 0),
                          )?.id ||
                          quickBooksTaxRates[0]?.id ||
                          ""
                        : "";
                      const defaultTaxRate = companyProfile?.plugins?.quickbooks
                        ?.connected
                        ? taxRateData.taxRate || "0"
                        : parseFloat(taxRateData.taxRate) || 0;
                      const defaultItemId = companyProfile?.plugins?.quickbooks
                        ?.connected
                        ? companyProfile.defaultTax?.defaultItemId ||
                          quickBooksItems[0]?.id ||
                          ""
                        : "";

                      const prevInvoiced = calculatePreviousInvoiced(
                        "Original Contract Value",
                        editingSovId,
                      );

                      const workThis = editingSovId
                        ? sovData.items.find(
                            (i) => i.description === "Original Contract Value",
                          )?.workCompletedThisPeriod || "0.00"
                        : "0.00";
                      const totalCompleted = (
                        prevInvoiced + parseFloat(workThis)
                      ).toFixed(2);

                      const originalItem = {
                        itemNumber: 1,
                        description: "Original Contract Value",
                        taxCodeId: defaultTaxCode,
                        taxRate: defaultTaxRate.toString(),
                        scheduledValue: originalContractValue.toFixed(2),
                        workCompletedPreviously: prevInvoiced.toFixed(2),
                        workCompletedThisPeriod: workThis,
                        totalCompletedToDate: totalCompleted,
                        percentage: originalContractValue
                          ? (
                              (parseFloat(totalCompleted) /
                                originalContractValue) *
                              100
                            ).toFixed(2)
                          : "0.00",
                        balanceToFinish: (
                          originalContractValue - parseFloat(totalCompleted)
                        ).toFixed(2),

                        qbItem: defaultItemId,
                        changeOrderNumber: null,
                      };

                      // Append change-orders (same logic as buildFinalItems)
                      const existingCoNumbers = new Set(
                        sovData.items
                          .filter((i) => i.changeOrderNumber)
                          .map((i) => String(i.changeOrderNumber)),
                      );

                      const changeOrderItems = approvedChangeOrders
                        .filter(
                          (co) =>
                            !existingCoNumbers.has(
                              String(co.changeOrderNumber),
                            ),
                        )
                        .map((co, idx) => {
                          const description = `${co.changeOrderNumber} - ${co.title}`;
                          const scheduledValue = parseFloat(co.totalPrice) || 0;
                          const prevInvoiced = calculatePreviousInvoiced(
                            description,
                            editingSovId,
                          );

                          const workThis = "0.00";
                          const totalCompleted = (
                            prevInvoiced + parseFloat(workThis)
                          ).toFixed(2);

                          return {
                            itemNumber: 1 + idx + 1,
                            description,
                            changeOrderNumber: String(co.changeOrderNumber),
                            changeOrderId: co._id,
                            taxCodeId: defaultTaxCode,
                            taxRate: defaultTaxRate.toString(),
                            scheduledValue: scheduledValue.toFixed(2),
                            workCompletedPreviously: prevInvoiced.toFixed(2),
                            workCompletedThisPeriod: workThis,
                            totalCompletedToDate: totalCompleted,
                            percentage: scheduledValue
                              ? (
                                  (parseFloat(totalCompleted) /
                                    scheduledValue) *
                                  100
                                ).toFixed(2)
                              : "0.00",
                            balanceToFinish: (
                              scheduledValue - parseFloat(totalCompleted)
                            ).toFixed(2),

                            qbItem: defaultItemId,
                          };
                        });

                      setSovData((prev) => ({
                        ...prev,
                        items: [originalItem, ...changeOrderItems],
                      }));

                      setIsBreakdownExpanded(false);
                    } else {
                      // ---- CUSTOM BREAKDOWN ON ----
                      // Use the current custom breakdown list + change-orders
                      setSovData((prev) => ({
                        ...prev,
                        items: buildFinalItems(
                          customBreakdownItems,
                          prev.items,
                        ),
                      }));
                      setIsBreakdownExpanded(true);
                    }
                  }}
                  disabled={sovData.status === "voided"}
                  className="mt-1 h-4 w-4 text-orange-400 focus:ring-orange-500 border-gray-500 rounded disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>

            {isCustomBreakdown && (
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-400">
                    Original Contract Breakdown (Total:{" "}
                    {formatDollarValue(project.originalContractValue)})
                  </h4>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-400">
                      Remaining:{" "}
                      {formatDollarValue(calculateRemainingBreakdown())}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setIsBreakdownExpanded(!isBreakdownExpanded)
                      }
                      disabled={sovData.status === "voided"}
                      className={`text-sm font-medium ${
                        sovData.status === "voided"
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-orange-400 hover:text-orange-300"
                      } transition-colors duration-200`}
                    >
                      {isBreakdownExpanded ? "Collapse ▼" : "Expand ▲"}
                    </button>
                  </div>
                </div>
                {isBreakdownExpanded && (
                  <div className="space-y-2 mt-2">
                    {customBreakdownItems.map((item, index) => {
                      const sovItem = sovData.items.find(
                        (sovItem) =>
                          sovItem.description ===
                          (item.description || `Breakdown Item ${index + 1}`),
                      );
                      const hasProgress =
                        sovItem &&
                        ((parseFloat(sovItem.workCompletedPreviously) || 0) >
                          0 ||
                          (parseFloat(sovItem.workCompletedThisPeriod) || 0) >
                            0);
                      return (
                        <div
                          key={index}
                          className="flex space-x-2 items-center"
                        >
                          <input
                            type="text"
                            placeholder="Description (e.g., First Floor)"
                            value={item.description}
                            onChange={(e) =>
                              handleBreakdownItemChange(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            disabled={sovData.status === "voided"}
                            className="flex-1 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          />
                          <NumericFormat
                            value={item.amount}
                            onValueChange={(values) =>
                              handleBreakdownItemChange(
                                index,
                                "amount",
                                values.floatValue || "0.00",
                              )
                            }
                            thousandSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            disabled={sovData.status === "voided"}
                            className="w-32 rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                            placeholder="$0.00"
                          />
                          <button
                            type="button"
                            onClick={() => removeBreakdownItem(index)}
                            disabled={
                              sovData.status === "voided" ||
                              customBreakdownItems.length === 1 ||
                              hasProgress
                            }
                            className="text-red-400 hover:text-red-300 disabled:text-gray-400 transition-colors duration-200"
                          >
                            🗑️
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={addBreakdownItem}
                      disabled={sovData.status === "voided"}
                      className={`text-sm font-medium ${
                        sovData.status === "voided"
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-orange-400 hover:text-orange-300"
                      } transition-colors duration-200`}
                    >
                      + Add Breakdown Item
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="overflow-hidden rounded-lg shadow-xl">
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-4 rounded-tl-lg">
                      Item #
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-48">
                      Description
                    </th>
                    {companyProfile?.plugins?.quickbooks?.connected && (
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-48">
                        QuickBooks Item
                      </th>
                    )}
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-28">
                      Tax Rate
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Scheduled Value
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Work Completed (Prev)
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Work Completed (This)
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Total Completed
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-16">
                      %
                    </th>

                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-32">
                      Balance to Finish
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase w-24 rounded-tr-lg">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {sovData.items.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-600 transition-colors duration-200"
                    >
                      <td className="px-2 py-2 text-sm text-gray-200 w-12">
                        {item.itemNumber}
                      </td>
                      <td className="px-2 py-2 w-48">
                        <textarea
                          value={item.description}
                          onChange={(e) =>
                            handleItemChange(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          disabled={sovData.status === "voided"}
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50 min-h-[4rem] resize-y"
                          readOnly={
                            !companyProfile?.plugins?.quickbooks?.connected
                          }
                          rows="3"
                          placeholder="Enter description"
                        />
                      </td>
                      {companyProfile?.plugins?.quickbooks?.connected && (
                        <td className="px-2 py-2 w-48">
                          <select
                            value={item.qbItem || ""}
                            onChange={(e) =>
                              handleItemChange(index, "qbItem", e.target.value)
                            }
                            disabled={sovData.status === "voided"}
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          >
                            <option value="">Select QB Item</option>
                            {quickBooksItems.map((qbItem) => (
                              <option key={qbItem.id} value={qbItem.id}>
                                {qbItem.name}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td className="px-2 py-2 w-20">
                        {companyProfile?.plugins?.quickbooks?.connected ? (
                          <select
                            value={item.taxCodeId}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "taxCodeId",
                                e.target.value,
                              )
                            }
                            disabled={sovData.status === "voided"}
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          >
                            {quickBooksTaxRates.map((tax) => (
                              <option key={tax.id} value={tax.id}>
                                {`${tax.name} (${tax.taxRates[0]?.rate || 0}%)`}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <NumericFormat
                            value={item.taxRate}
                            onValueChange={(values) =>
                              handleItemChange(
                                index,
                                "taxRate",
                                values.floatValue || "0.00",
                              )
                            }
                            decimalScale={2}
                            fixedDecimalScale
                            disabled={sovData.status === "voided"}
                            className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                            placeholder="0.00"
                          />
                        )}
                      </td>
                      <td className="px-2 py-2 w-28">
                        <NumericFormat
                          value={item.scheduledValue}
                          onValueChange={(values) =>
                            handleItemChange(
                              index,
                              "scheduledValue",
                              values.floatValue || "0.00",
                            )
                          }
                          thousandSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$"
                          disabled={sovData.status === "voided"}
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          placeholder="$0.00"
                        />
                      </td>
                      <td className="px-2 py-2 w-28">
                        <input
                          type="text"
                          value={formatDollarValue(
                            item.workCompletedPreviously,
                          )}
                          readOnly
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 sm:text-sm opacity-50"
                        />
                      </td>
                      <td className="px-2 py-2 w-28">
                        <NumericFormat
                          value={item.workCompletedThisPeriod}
                          onValueChange={(values) =>
                            handleItemChange(
                              index,
                              "workCompletedThisPeriod",
                              values.floatValue || "0.00",
                            )
                          }
                          thousandSeparator=","
                          decimalScale={2}
                          fixedDecimalScale
                          prefix="$"
                          disabled={sovData.status === "voided"}
                          className="w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm disabled:bg-gray-600 disabled:text-gray-400 disabled:opacity-50"
                          placeholder="$0.00"
                        />
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-200 w-28">
                        {formatDollarValue(item.totalCompletedToDate)}
                      </td>

                      <td className="px-2 py-2 text-sm text-gray-200 w-16">
                        {item.percentage}
                      </td>
                      <td className="px-2 py-2 text-sm text-gray-200 w-28">
                        {formatDollarValue(item.balanceToFinish)}
                      </td>
                      <td className="px-2 py-2 flex space-x-2 w-24">
                        <button
                          type="button"
                          onClick={() => moveItemUp(index)}
                          disabled={index === 0 || sovData.status === "voided"}
                          className="text-orange-400 hover:text-orange-300 disabled:text-gray-400 transition-colors duration-200"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveItemDown(index)}
                          disabled={
                            index === sovData.items.length - 1 ||
                            sovData.status === "voided"
                          }
                          className="text-orange-400 hover:text-orange-300 disabled:text-gray-400 transition-colors duration-200"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          disabled={sovData.status === "voided"}
                          className="text-red-400 hover:text-red-300 disabled:text-gray-400 transition-colors duration-200"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-800 font-semibold">
                    <td className="px-2 py-2 text-sm text-gray-200 w-12"></td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-48">
                      Total
                    </td>
                    {companyProfile?.plugins?.quickbooks?.connected && (
                      <td className="px-2 py-2 w-48"></td>
                    )}
                    <td className="px-2 py-2 w-20"></td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalScheduledValue)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalWorkCompletedPreviously)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalWorkCompletedThisPeriod)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalCompletedToDate)}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-16">
                      {totalPercentage}
                    </td>
                    <td className="px-2 py-2 text-sm text-gray-200 w-28">
                      {formatDollarValue(totalBalanceToFinish)}
                    </td>
                    <td className="px-2 py-2 w-24"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-400">Subtotals</h4>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-400">
                    This Period Subtotal:
                  </label>
                  <span className="text-gray-200">
                    {formatDollarValue(thisPeriodSubtotal)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm text-gray-400">HST:</label>
                  <span className="text-gray-200">
                    {formatDollarValue(hst)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm text-gray-400">
                    Sub-Total with Tax:
                  </label>
                  <span className="text-gray-200">
                    {formatDollarValue(subTotalWithTax)}
                  </span>
                </div>
                {sovData.withHoldback && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-400">
                        Current Holdback (10%):
                      </label>
                      <span className="text-gray-200">
                        {formatDollarValue(currentHoldback)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400">
                        Total Project Holdback:
                      </label>
                      <span className="text-gray-200">
                        {formatDollarValue(totalProjectHoldback)}
                      </span>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm text-gray-400">
                    Remaining Balance:
                  </label>
                  <span className="text-gray-200">
                    {formatDollarValue(remainingBalance)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  window.scrollTo(0, 0); // Scroll to top
                }}
                className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200"
              >
                Cancel
              </button>
              {editingSovId && (
                <>
                  <button
                    type="button"
                    onClick={() => generateSOVPDF(true, false)}
                    disabled={sovData.status === "voided"}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      sovData.status === "voided"
                        ? "bg-gray-600 cursor-not-allowed opacity-50"
                        : "bg-orange-400 hover:bg-orange-500"
                    } transition-colors duration-200`}
                  >
                    PDF SOV
                  </button>
                  <button
                    type="button"
                    onClick={handleSendEmail}
                    disabled={sovData.status === "voided"}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      sovData.status === "voided"
                        ? "bg-gray-600 cursor-not-allowed opacity-50"
                        : "bg-orange-400 hover:bg-orange-500"
                    } transition-colors duration-200`}
                  >
                    Send Email
                  </button>
                </>
              )}
              {companyProfile?.plugins?.quickbooks?.connected && (
                <>
                  {sovData.qbInvoiceNumber && (
                    <>
                      <button
                        type="button"
                        onClick={voidQBInvoice}
                        disabled={sovData.status === "voided"}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                          sovData.status === "voided"
                            ? "bg-gray-600 cursor-not-allowed opacity-50"
                            : "bg-red-400 hover:bg-red-500"
                        } transition-colors duration-200`}
                      >
                        Void QB Invoice
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={generateQBInvoice}
                    disabled={
                      sovData.status === "voided" || isGeneratingInvoice
                    }
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                      sovData.status === "voided" || isGeneratingInvoice
                        ? "bg-gray-600 cursor-not-allowed opacity-50"
                        : "bg-orange-400 hover:bg-orange-500"
                    } transition-colors duration-200`}
                  >
                    {isGeneratingInvoice
                      ? "Processing..."
                      : sovData.qbInvoiceNumber
                        ? "Update SOV & Invoice"
                        : "Generate QB Invoice"}
                  </button>
                </>
              )}
              {!sovData.qbInvoiceNumber && (
                <button
                  type="submit"
                  disabled={sovData.status === "voided"}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                    sovData.status === "voided"
                      ? "bg-gray-600 cursor-not-allowed opacity-50"
                      : "bg-orange-400 hover:bg-orange-500"
                  } transition-colors duration-200`}
                >
                  {editingSovId ? "Update SOV" : "Save SOV"}
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
